import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {InlineLink} from 'UI/components/action/InlineLink';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {H6, P} from 'UI/components/layout/Text';
import {usePartnerCard} from 'UI/components/modules/Steps/PartnerCard/usePartnerCard';

import {FadeInAnimation} from 'UI/animations/shared';

import {LogoMnetInv, LogoTelekomInv} from 'Images/icons';

import type {LinkField, Media} from 'types/payload-types';

/**
 * The props for the `PartnerCard` component, which displays a partner-focused
 * card with a hotline link, an optional headline, and a call-to-action button.
 * This interface defines the structure of the content required to render the card,
 * including optional hotline text, an image, and descriptive text. It also provides
 * a `testId` for reliable end-to-end testing.
 */
interface ComponentProps {
    /**
     * The configuration for the call-to-action (CTA) button.
     * Defines the label, type, and destination for the partner action link.
     */
    button?: LinkField;
    /**
     * Optional headline text displayed at the top of the card.
     * Used to introduce or label the content of the `PartnerCard`.
     */
    headline?: string | null;
    /**
     * The hotline link configuration, defining the phone number or other
     * clickable action for partner support.
     */
    hotlineLink?: LinkField;
    /**
     * Optional supporting text displayed next to the hotline link.
     * Often used to provide context, such as "Rufen Sie uns an:".
     */
    hotlineText?: string | null;
    /**
     * The optional image displayed at the top of the `PartnerCard`.
     * Typically used for an icon, illustration, or supporting graphic.
     */
    image?: Media | string | null;
    /**
     * Specifies the partner type to conditionally render the appropriate logo overlay.
     */
    partner?: 'mnet' | 'telekom';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'PartnerCard'
     */
    testId?: string;
}

/**
 * A card component that highlights partner-specific information with a hotline link and CTA button.
 * The `PartnerCard` displays an image, a headline, optional hotline text with a linked phone number,
 * and a call-to-action button. Both the hotline link and the button link are normalized using
 * the `usePartnerCard` hook, which resolves attributes such as `href`, `label`, `rel`, and `target`.
 * The component is wrapped in MobX’s `observer`, making it reactive to observable data changes.
 * A `data-cy` attribute is applied to the wrapper for reliable targeting in end-to-end testing.
 *
 * @param props             The component props.
 * @param props.button      The CTA button configuration, including label and link details.
 * @param props.headline    Optional headline text displayed at the top of the card.
 * @param props.hotlineLink The hotline link configuration, typically a phone number link.
 * @param props.hotlineText Optional supporting text displayed above the hotline link.
 * @param props.image       The image displayed at the top of the card, usually an icon or illustration.
 * @param props.testId      An optional identifier used for testing, applied as `data-cy`. Defaults to `'PartnerCard'`.
 * @returns A JSX element rendering the partner card with image, text, hotline link, and CTA button.
 *
 * @example
 * ```tsx
 * <PartnerCard
 *   headline="Partner Hotline"
 *   hotlineText="Für unsere Partner:"
 *   hotlineLink={{ label: "0800 987 654", linkType: "phone", url: "tel:0800987654" }}
 *   button={{ label: "Mehr erfahren", linkType: "internal", internal: "/partners" }}
 *   image={partnerImage}
 * />
 * ```
 */
const PartnerCard = observer(({
    button,
    headline,
    hotlineLink,
    hotlineText,
    image,
    partner,
    testId = 'PartnerCard'
}: ComponentProps) => {
    const {buttonHref, buttonLabel, href, label, rel, target} = usePartnerCard(hotlineLink, button);

    return (
        <Wrapper data-cy={testId} variants={FadeInAnimation}>
            <ImageWrapper variants={FadeInAnimation}>
                <Image
                    expectedSizes={{
                        xs: 522, sm: 708, md: 350, lg: 446, xl: 446, xxl: 446
                    }}
                    image={image}
                    isPresentational
                />
                <IconOverlay>
                    {partner === 'mnet' && <LogoMnetInv height={19} width={52} />}
                    {partner === 'telekom' && <LogoTelekomInv height={23} width={28} />}
                </IconOverlay>
            </ImageWrapper>
            <TextWrapper>
                {headline && <MotionH6 as="h4" variants={FadeInAnimation}>{headline}</MotionH6>}
                {hotlineText && (
                    <MotionP variants={FadeInAnimation}>
                        {hotlineText}<br /><InlineLink href={href!}>{label}</InlineLink>
                    </MotionP>
                )}
                <MotionButton
                    as="link"
                    href={buttonHref}
                    rel={rel}
                    target={target}
                    variants={FadeInAnimation}
                >
                    {buttonLabel}
                </MotionButton>
            </TextWrapper>
        </Wrapper>
    );
});

PartnerCard.displayName = 'PartnerCard';

export {PartnerCard};

const Wrapper = styled(motion.div)`
    border: 1px solid ${({theme}) => theme.colors.neutral200};
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    width: 100%;
`;

const ImageWrapper = styled(motion.div)`
    border-radius: 24px 24px 0 0;
    height: 20rem;
    position: relative;
    width: 100%;
`;

const Image = styled(PayloadImage)`
    border-radius: 24px 24px 0 0;
    height: 20rem;
    object-fit: cover;
    width: 100%;
`;

const IconOverlay = styled.div`
    align-items: center;
    backdrop-filter: blur(9.538597106933594px);
    background: rgba(244, 244, 245, 0.20);
    border: 1px solid rgba(255, 255, 255, 0.40);
    border-radius: 24px;
    display: flex;
    height: 72px;
    justify-content: center;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 72px;
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
    padding: ${spacing(12)} ${spacing(12)} ${spacing(12)} ${spacing(12)};
    width: 100%;
`;

const MotionH6 = styled(motion.create(H6))``;

const MotionP = styled(motion.create(P))``;

const MotionButton = styled(motion.create(Button))``;